/**
 * UTM capture for campaign attribution.
 *
 * Reads the 5 standard utm_* params from a query string and returns a flat
 * object of only the ones present. Used to attribute landing-page traffic
 * (e.g. the Reddit deposit test) to its source.
 *
 * PostHog already auto-captures UTMs at the session level; this helper
 * additionally attaches them to explicit funnel events (page_view, cta_click)
 * so they are queryable per-event and can be forwarded to Stripe for
 * per-deposit attribution via client_reference_id.
 *
 * Callers should pass react-router's `useLocation().search` (SPA-correct and
 * test-friendly under MemoryRouter); it falls back to window.location.search.
 */

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

export type UtmParams = Partial<Record<(typeof UTM_KEYS)[number], string>>;

export function getUtmParams(
  search: string = typeof window !== "undefined" ? window.location.search : "",
): UtmParams {
  const params = new URLSearchParams(search);
  const out: UtmParams = {};
  for (const key of UTM_KEYS) {
    const val = params.get(key);
    if (val) out[key] = val;
  }
  return out;
}

/**
 * Compact, Stripe-safe token for client_reference_id, built from source +
 * campaign. Stripe's client_reference_id accepts up to ~200 chars; we keep it
 * URL/identifier-safe ([A-Za-z0-9_-]). Returns null when there is no utm_source
 * (nothing meaningful to attribute).
 */
export function utmClientReferenceId(utm: UtmParams): string | null {
  if (!utm.utm_source) return null;
  const joined = [utm.utm_source, utm.utm_campaign].filter(Boolean).join("__");
  const safe = joined.replace(/[^A-Za-z0-9_-]/g, "-").slice(0, 190);
  return safe || null;
}
